import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

type IngestionEvent = {
  id: string;
  user_id: string;
  source_type: string;
  source_ref?: string | null;
  event_type: string;
  status?: string | null;
  details: any;
  created_at: string;
};

type AliasRow = {
  token: string;
  customer_id: string | null;
  user_id: string;
  active: boolean;
  created_at: string;
  updated_at: string;
};

type MailboxState = {
  mailbox_address: string;
  connected: boolean;
  last_sync_at?: string | null;
  last_error?: string | null;
};

export function useGmailIngestionAdmin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const aliasesQuery = useQuery<AliasRow[]>({
    queryKey: ["aliases", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase.from as any)("email_alias_map")
        .select("*")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AliasRow[];
    },
    enabled: !!user?.id,
  });

  const stateQuery = useQuery<MailboxState | null>({
    queryKey: ["gmail-state", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await (supabase.from as any)("gmail_mailbox_state")
        .select("mailbox_address,connected,last_sync_at,last_error")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as MailboxState | null;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const eventsQuery = useQuery<IngestionEvent[]>({
    queryKey: ["ingestion-events", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase.from as any)("ingestion_events")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as unknown as IngestionEvent[];
    },
    enabled: !!user?.id,
    refetchInterval: 15000,
  });

  const runHealth = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-gmail-health", {
        body: {},
      });
      if (error) throw error;
      return data as any;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gmail-state"] });
      toast({ title: "Health check complete", description: "Mailbox status updated." });
    },
    meta: { onError: (e: any) => console.error("Health error", e) },
  });

  const runIngestNow = useMutation({
    mutationFn: async (lookbackMinutes?: number) => {
      const { data, error } = await supabase.functions.invoke("admin-gmail-run-ingest", {
        body: { lookbackMinutes: lookbackMinutes ?? 60 },
      });
      if (error) throw error;
      return data as any;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingestion-events"] });
      qc.invalidateQueries({ queryKey: ["gmail-state"] });
      toast({ title: "Ingest started", description: "Processing recent emails." });
    },
    meta: { onError: (e: any) => console.error("Ingest error", e) },
  });

  const upsertAlias = useMutation({
    mutationFn: async ({ token, customer_id }: { token: string; customer_id?: string | null }) => {
      const { data, error } = await supabase.functions.invoke("intake-aliases", {
        body: { token, customer_id: customer_id ?? null },
      });
      if (error) throw error;
      return data as { token: string; address: string; customer_id: string | null };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aliases"] });
      toast({ title: "Alias saved", description: "Plus-address is ready to use." });
    },
    meta: { onError: (e: any) => console.error("Alias error", e) },
  });

  return {
    aliases: aliasesQuery.data ?? [],
    events: eventsQuery.data ?? [],
    state: stateQuery.data ?? null,
    isLoading: aliasesQuery.isLoading || eventsQuery.isLoading || stateQuery.isLoading,
    runHealth: runHealth.mutateAsync,
    runIngestNow: runIngestNow.mutateAsync,
    upsertAlias: upsertAlias.mutateAsync,
  };
}
